<script setup lang="ts">
import { FetchError } from 'ofetch'
import { App, Modules } from '@/types/generated'
import { formatDateToMonthShort } from '@core/utils/formatters'
import GameTabDescription from '@/views/game/GameTabDescription.vue'
import GameTabResults from '@/views/game/GameTabResults.vue'
import GameTabComments from '@/views/game/GameTabComments.vue'
import GameTabFeedback from '@/views/game/GameTabFeedback.vue'
import GameTabChecklist from '@/views/game/GameTabChecklist.vue'
import GameExpertAssignDialog from '@/components/dialogs/GameExpertAssignDialog.vue'
import GameSpeakerMappingDialog from '@/components/dialogs/GameSpeakerMappingDialog.vue'
import { useUserStore } from '@core/stores/user'
import { userIsExpert, userIsAdmin, userIsManager, userIsHead, hideAiResults } from '@/utils/helpers'
import GameData = Modules.Game.GameData
import UserData = App.Http.Controllers.Auth.UserData
import GameEffectivenessData = Modules.GameResults.GameEffectivenessData
import EvaluationRequestData = Modules.Evaluation.EvaluationRequestData
import EvaluatorTypeEnum = Modules.Evaluation.Enums.EvaluatorTypeEnum
import GameParticipantData = Modules.Game.GameParticipantData
import OrganizationSettingsData = Modules.Organization.OrganizationSettingsData
import EvaluationRequestStatusEnum = Modules.Evaluation.Enums.EvaluationRequestStatusEnum
import GameTypeEnum = Modules.Shared.Enums.GameTypeEnum
import GameStatusEnum = Modules.Shared.Enums.GameStatusEnum
import GameParticipantRoleEnum = Modules.Shared.Enums.GameParticipantRoleEnum
import EvaluationFormatEnum = Modules.Rubric.Enums.EvaluationFormatEnum
import RoleEnum = Modules.Shared.Enums.RoleEnum
import { useApi } from '@/composables/useApi'
import { getDateLocale } from '@core/utils/locale'
import { getVideoDisplayState } from '@/utils/videoProcessingState'
import { hasOpposingTeamsScore } from '@/utils/gameScoreLayout'
import SpeakerMappingReviewData = Modules.AI.SpeakerMappingReviewData
import SpeakerMappingStatusEnum = Modules.Evaluation.Enums.SpeakerMappingStatusEnum
import AIRerunRejectionReasonEnum = Modules.Evaluation.Enums.AIRerunRejectionReasonEnum

const route = useRoute('games-detail-id')
const userData = useCookie<UserData>('userData')
const organizationSettings = useCookie<OrganizationSettingsData>('organizationSettings')
const { locale } = useI18n({ useScope: 'global' })
const { t } = useI18n()
const userRoles: string [] = userData.value?.roles ?? []
const hideAi = hideAiResults(userRoles, organizationSettings.value)
const isGameLoading = ref(true)
const isNotFound = ref(false)

const dateLocale = getDateLocale(locale.value)

definePage({
  meta: {
    action: 'read',
    subject: 'games',
  },
})

const userTab = ref<string>('comments')

const isDialogVisible = ref(false)
const AIClicked = ref(false)

watch(isDialogVisible, value => {
  if (!value)
    return

  setTimeout(() => {
    isDialogVisible.value = false
    AIClicked.value = true
  }, 4000)
})

interface Item {
  id: string
  title: string
}
const { data: statusesList } = await useApi<any>('/games/statuses')

let statuses: Item[] = [{ title: '', id: '1' }]
if (statusesList?.value)
  statuses = statusesList.value
function findTitleById(items: Item[], id: string): string | undefined {
  const item: any = items.find((i: any) => i.id === id)

  return item?.title
}
const AICreated = ref<boolean>(false)
const ExpertRequestAlreadyCreated = ref<boolean>(false)

const resolveStatusVariant = (status: string) => {
  if (status === 'time_scheduling')
    return { color: 'primary', text: findTitleById(statuses, status) }
  else if (status === 'completed')
    return { color: 'success', text: findTitleById(statuses, status) }
  else if (status === 'failed')
    return { color: 'error', text: findTitleById(statuses, status) }
  else if (status === 'waiting_for_start')
    return { color: 'warning', text: findTitleById(statuses, status) }
  else if (status === 'missed')
    return { color: 'secondary', text: findTitleById(statuses, status) }
  else
    return { color: 'info', text: findTitleById(statuses, status) }
}
const isUuid = (value: unknown): value is string => {
  if (typeof value !== 'string') return false
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
  return uuidRegex.test(value)
}
const gameData = ref<GameData>({
  id: '',
  number: 0,
  status: Modules.Shared.Enums.GameStatusEnum.FAILED,
  type: GameTypeEnum.PLAYER_VS_PLAYER,
  scenarioId: '',
  organizationId: '',
  expertId: '',
  createdBy: '',
  startedBy: '',
  startTimeSuggestedBy: '',
  endedBy: '',
  tourId: '',
  startUpTo: '',
  startTime: '',
  startedAt: '',
  endedAt: '',
  duration: 0,
  preparationTime: 0,
  protocolTime: 0,
  participants: [],
  recordUrl: '',
  isStartTimeAgreed: false,
  isExpertAgreed: false,
  autoShowResults: false,
  isCameraRequired: false,
  isProtocolProposed: false,
  isSelfRated: false,
  isEachOtherRated: false,
  isExpertRated: false,
  isAiRated: false,
  isTest: false,
  isPublic: false,
  seriesId: null,
  seriesPosition: null,
  isResultsVisible: false,
  competenceTitle: '',
  evaluationFormat: null,
})

const isChecklist = computed(() => gameData.value.evaluationFormat === EvaluationFormatEnum.BinaryChecklist)

const isGameCompleted = computed(() => gameData.value.status === GameStatusEnum.COMPLETED)

// Наблюдатели и приглашённые руководители лежат в тех же participants, что и игроки, но
// оценивать, комментировать и считать по счёту нужно только игроков.
const gamePlayers = computed<GameParticipantData[]>(() => (gameData.value.participants ?? []).filter(
  participant => participant.role === GameParticipantRoleEnum.PLAYER || participant.role === GameParticipantRoleEnum.CAPTAIN,
))

const isOpposingTeamsScore = computed(() => hasOpposingTeamsScore(gameData.value.type, gamePlayers.value))

const tabs = computed(() => {
  if (isChecklist.value) {
    return [
      { title: 'comments', icon: 'tabler-brand-youtube' },
      { title: 'checklist', icon: 'tabler-checkup-list' },
      { title: 'description', icon: 'tabler-presentation' },
    ]
  }

  return [
    { title: 'comments', icon: 'tabler-brand-youtube' },
    { title: 'feedback', icon: 'tabler-edit' },
    { title: 'results', icon: 'tabler-report-analytics' },
    { title: 'description', icon: 'tabler-presentation' },
  ]
})

const userIsCurrentGamePlayer = ref<boolean>(false)
const userIsCaptain = ref<boolean>(false)
const userIsGameExpert = ref<boolean>(false)
const isGameExpertDialogOpen = ref<boolean>(false)
const isGameExpertAssignDialogOpen = ref<boolean>(false)
const isLoaderDialogVisible = ref<boolean>(false)
const isVideoProcessing = ref<boolean>(false)

const getGameData = async (maybeId?: string): Promise<boolean> => {
  const id = maybeId ?? route.params.id
  if (!isUuid(id)) {
    console.warn('Invalid uuid: ' + id)
    return false
  }

  const { data: apiData } = await useApi<GameData>(`/games/${id}`)
  if (apiData.value) {
    gameData.value = apiData.value

    userIsCurrentGamePlayer.value = gameData.value.participants?.some(
      (p: any) => p.user.id === userData.value.id && (p.role === 'captain' || p.role === 'player'),
    ) === true

    userIsCaptain.value = gameData.value.participants?.some(
      (p: any) => p.user.id === userData.value.id && p.role === 'captain',
    ) === true

    return gameData.value.number !== 0 && !!gameData.value.id
  }

  return false
}

const isGameVsPlayer = () => {
  return gameData.value.type === GameTypeEnum.PLAYER_VS_PLAYER
}

const isGameVsAI = () => {
  return gameData.value.type === GameTypeEnum.PLAYER_VS_AI
}

const isGameUploadedRecording = () => {
  return gameData.value.type === GameTypeEnum.UPLOADED_RECORDING
}
const router = useRouter()

const getVoiceAgentProgress = async (maybeId?: string) => {
  const id = maybeId ?? route.params.id
  if (!isUuid(id)) return

  const { data } = await useApi<any>(`/voice-agent/game-state/${id}`)
  if (data?.value) {
    const isFailed = gameData.value.status === Modules.Shared.Enums.GameStatusEnum.FAILED

    isLoaderDialogVisible.value = data.value?.has_results !== true && !isFailed
    isVideoProcessing.value = getVideoDisplayState(
      gameData.value.recordUrl,
      data.value?.is_processed ?? false,
      data.value?.recording_state ?? null,
      isFailed,
    ) === 'processing'
  }
}

const hideGameNumber = !organizationSettings?.value?.allowPlayersChooseExpert && userIsPlayer(userRoles)

const gameEffectiveness = ref<GameEffectivenessData[]>()

const evaluationRequests = ref<EvaluationRequestData[]>([])
const effectivenessTeamA = ref<GameEffectivenessData>()
const effectivenessTeamB = ref<GameEffectivenessData>()
const showRequestExpertButton = ref<boolean>(false)
const loadingDeletingAIRequest = ref<boolean>(false)

const winnerIsA = ref<boolean>(false)

const resolveAvatarVariant = (role: string) => {
  if (role === 'player')
    return 'primary'
  else if (role === 'captain')
    return 'warning'
  else if (role === 'observer')
    return 'secondary'
  else
    return 'info'
}

// Действия над участниками AI-оценки открыты тем же ролям, что и на бэкенде: role:manager|admin.
const userCanFixParticipants = userIsAdmin(userRoles) || userRoles.includes(RoleEnum.MANAGER)

const emptyAIRequest = (): EvaluationRequestData => ({
  id: '',
  gameId: '',
  requestedBy: '',
  evaluatorId: '',
  status: null,
  evaluatorType: EvaluatorTypeEnum.AI,
  speakerMappingId: null,
  reevaluationInProgress: false,
})

const AIRequest = ref<EvaluationRequestData>(emptyAIRequest())

// Исправление участников и кнопка «Обновить результаты AI» ставят переоценку, но статус запроса
// не меняют. Сразу после действия индикатор держит этот флаг, а с первого
// события `.game.should.refresh` — признак с бэкенда.
const reevaluationRequested = ref(false)

// Идёт ли оценка на самом деле. По этому признаку страница перечитывает вкладки, когда
// переоценка закончилась, — это нужно всем ролям, даже тем, кому её ход не показывают.
const aiEvaluationRunning = computed(() => AIRequest.value.status === EvaluationRequestStatusEnum.IN_PROGRESS
  || AIRequest.value.reevaluationInProgress === true
  || reevaluationRequested.value)

// Что показывать. Ход переоценки видит тот, кто может на неё повлиять; игроку, как и до ветки,
// остаётся индикатор первичной оценки по статусу запроса.
const aiEvaluationInProgress = computed(() => AIRequest.value.status === EvaluationRequestStatusEnum.IN_PROGRESS
  || (userCanFixParticipants && aiEvaluationRunning.value))

const emptyExpertRequest = (): EvaluationRequestData => ({
  id: '',
  gameId: '',
  requestedBy: '',
  evaluatorId: '',
  status: null,
  evaluatorType: EvaluatorTypeEnum.EXPERT,
  speakerMappingId: null,
  reevaluationInProgress: false,
})

const ExpertRequest = ref<EvaluationRequestData>(emptyExpertRequest())

const aiEvaluationBlockReason = ref<AIRerunRejectionReasonEnum | null>(null)

const aiEvaluationBlockReasonKeys: Record<string, string> = {
  [AIRerunRejectionReasonEnum.RecognitionHasNoSpeech]: 'game.ai_expert_unavailable.recognition_has_no_speech',
  [AIRerunRejectionReasonEnum.RecognitionHasTooLittleSpeech]: 'game.ai_expert_unavailable.recognition_has_too_little_speech',
  [AIRerunRejectionReasonEnum.GameEvaluationBlocked]: 'game.ai_expert_unavailable.game_evaluation_blocked',
}

const aiEvaluationBlockMessage = computed(() => {
  const key = aiEvaluationBlockReason.value === null ? undefined : aiEvaluationBlockReasonKeys[aiEvaluationBlockReason.value]

  return key ? t(key) : ''
})

const getAIEvaluationBlockReason = async (id: string) => {
  if (AIRequest.value.id !== '' || hideAi || isGameVsAI()) {
    aiEvaluationBlockReason.value = null

    return
  }

  const { data: apiData } = await useApi<{ reason: AIRerunRejectionReasonEnum | null }>(`/evaluations/${id}/ai-evaluation-block`)

  aiEvaluationBlockReason.value = apiData.value?.reason ?? null
}

const getEffectiveness = async (maybeId?: string) => {
  const id = maybeId ?? route.params.id
  if (!isUuid(id)) return
  const { data: apiData } = await useApi<GameEffectivenessData[]>(`/game-results/${id}/get-effectiveness`)

  if (apiData.value) {
    gameEffectiveness.value = apiData.value
    effectivenessTeamA.value = gameEffectiveness.value[0]?.team === 'A' ? gameEffectiveness.value[0] : gameEffectiveness.value[1]
    effectivenessTeamB.value = gameEffectiveness.value[0]?.team === 'B' ? gameEffectiveness.value[0] : gameEffectiveness.value[1]

    winnerIsA.value = (effectivenessTeamA.value?.total ?? 0) > (effectivenessTeamB.value?.total ?? 0)
  }
}

const getEvaluationRequests = async (maybeId?: string) => {
  const id = maybeId ?? route.params.id
  if (!isUuid(id)) return

  const { data: apiData } = await useApi<EvaluationRequestData[]>(`/evaluations/${id}/get-requests`)

  if (!apiData.value) return

  evaluationRequests.value = apiData.value

  // Снятый AI-запрос не должен оставлять на странице статус прежнего.
  AIRequest.value = evaluationRequests.value.find(request => request.evaluatorType === EvaluatorTypeEnum.AI) ?? emptyAIRequest()

  // Когда все экспертные запросы отменены, выборка пуста: шаблон местами читает
  // `ExpertRequest.status` без `?.`, поэтому вместо undefined кладём пустой запрос.
  if (evaluationRequests.value.filter(request => request.evaluatorType === EvaluatorTypeEnum.EXPERT).length)
    ExpertRequest.value = evaluationRequests.value.filter(request => request.evaluatorType === EvaluatorTypeEnum.EXPERT && request.status !== 'canceled')[0] ?? emptyExpertRequest()

  showRequestExpertButton.value = evaluationRequests.value.filter(item => item.evaluatorType === 'expert' && item.status !== 'canceled').length === 0
    && (userIsManager(userRoles) || userIsAdmin(userRoles) || userIsHead(userRoles) || userIsCaptain.value)

  if (!organizationSettings?.value?.allowPlayersChooseExpert && userIsPlayer(userRoles))
    showRequestExpertButton.value = false

  userIsGameExpert.value = evaluationRequests.value.filter(
    request => request.evaluatorId === userData.value.id).length === 1

  if (userIsGameExpert.value) {
    ExpertRequest.value = evaluationRequests.value.filter(
      request => request.evaluatorId === userData.value.id)[0]
  }

  await getAIEvaluationBlockReason(id)
}

const getParticipantHint = (participant: GameParticipantData) => {
  let res = `${participant.user.profile.name} ${participant.user.profile.last_name}`
  if (participant.joinedAt !== null)
    res += `<br>${t('game.joined_at')} ${formatDateToMonthShort(participant?.joinedAt ?? '2024-10-10 10:10', true, dateLocale, userData.value.timezone)}`

  return res
}

const createAIRequest = async () => {
  AIRequest.value.status = EvaluationRequestStatusEnum.QUEUED
  try {
    // 409 — отказ по существу (игра не допущена к оценке, запись без речи), а не сбой: без retry: 0 $api повторил бы его.
    const res = await $api('/evaluations/create-request', {
      method: 'POST',
      retry: 0,
      body: {
        game_id: gameData.value.id,
        evaluator_type: EvaluatorTypeEnum.AI,
      },
    })

    await getEvaluationRequests()
    if (res?.already_exists === true)
      ExpertRequestAlreadyCreated.value = true
    else
      AICreated.value = true
  }
  catch (err) {
    console.error(err)

    if (err instanceof FetchError && err.statusCode === 409)
      await getEvaluationRequests()
  }
}

const userStore = useUserStore()

const deleteAIRequest = async () => {
  const id = route.params.id
  if (!isUuid(id)) return

  loadingDeletingAIRequest.value = true
  await getGameData(id)
  AIRequest.value.status = null
  try {
    await $api(`/evaluations/${id}/ai-request`, { method: 'DELETE' })
    await getGameData(id)
  } finally {
    loadingDeletingAIRequest.value = false
  }
  userStore.shouldUpdateGameComments = true
  userStore.shouldUpdateGameEvaluation = true
}

const expertComplete = async () => {
  await $api(`/evaluations/${gameData.value.id}/complete-request`, {
    method: 'PUT',
  })

  await getEvaluationRequests()
}

const showAIExpertRefreshBtn = () => {
  return userIsAdmin(userRoles) && (AIRequest.value.id !== '' && AIRequest.value.status === EvaluationRequestStatusEnum.COMPLETED) && gameData.value.isAiRated
    && !aiEvaluationInProgress.value
}

const showAIExpertRequestBtn = () => {
  return !hideAi && (!userIsGameExpert.value || userIsManager(userRoles) || userIsHead(userRoles)) && gameData.value.isAiRated && Boolean(gameData.value.recordUrl)
}

const showDeleteAIRequestBtn = () => {
  return userIsAdmin(userRoles) && AIRequest.value.id !== '' && AIRequest.value.status === EvaluationRequestStatusEnum.COMPLETED
    && !aiEvaluationInProgress.value
}

const speakerMapping = ref<SpeakerMappingReviewData | null>(null)
const isSpeakerMappingDialogOpen = ref(false)
const loadingConfirmParticipants = ref(false)
const loadingFixParticipants = ref(false)
const speakerMappingSuccess = ref(false)
const speakerMappingSuccessMessage = ref('')
const speakerMappingError = ref(false)
const speakerMappingErrorMessage = ref('')

const getSpeakerMapping = async (maybeId?: string) => {
  const id = maybeId ?? route.params.id

  // Сброс обязателен: при переходе на другую игру на той же странице блок прежней не должен остаться.
  if (!isUuid(id) || !userCanFixParticipants || hideAi || isGameVsAI()) {
    speakerMapping.value = null

    return
  }

  const { data: apiData } = await useApi<{ speakerMapping: SpeakerMappingReviewData | null }>(`/evaluations/${id}/speaker-mapping`)

  speakerMapping.value = apiData.value?.speakerMapping ?? null
}

const speakerMappingParticipants = computed(() => (gameData.value.participants ?? []).map(participant => ({
  id: participant.user.id,
  fullName: `${participant.user.profile.name} ${participant.user.profile.last_name}`,
})))

// Алиасы `import X = Modules...` компилятор SFC в шаблон не отдаёт: там они становятся `_ctx.X`,
// то есть undefined, и рендер падает. Поэтому сравнения с enum живут в скрипте.
const speakerMappingReviewHint = computed(() => {
  if (speakerMapping.value?.status === SpeakerMappingStatusEnum.Failed)
    return t('game.speaker_mapping.failed')

  if (speakerMapping.value?.status === SpeakerMappingStatusEnum.Unavailable)
    return t('game.speaker_mapping.unavailable')

  if (speakerMapping.value?.status === SpeakerMappingStatusEnum.Empty)
    return t('game.speaker_mapping.empty')

  return t('game.speaker_mapping.needs_review_hint')
})

const reportSpeakerMappingError = (error: any) => {
  speakerMappingErrorMessage.value = error?.data?.message ?? error?.response?._data?.message ?? ''
  speakerMappingError.value = true
}

const confirmParticipants = async () => {
  loadingConfirmParticipants.value = true
  try {
    await $api(`/evaluations/${gameData.value.id}/confirm-participants`, { method: 'POST' })

    isSpeakerMappingDialogOpen.value = false
    speakerMappingSuccessMessage.value = t('game.speaker_mapping.confirmed')
    speakerMappingSuccess.value = true
    await getSpeakerMapping()
  }
  catch (error: any) {
    reportSpeakerMappingError(error)
  }
  finally {
    loadingConfirmParticipants.value = false
  }
}

const saveSpeakerMapping = async (assignments: { speaker: number; player_id: string }[]) => {
  loadingFixParticipants.value = true
  try {
    await $api(`/evaluations/${gameData.value.id}/speaker-mapping`, {
      method: 'PUT',
      body: { assignments },
    })

    reevaluationRequested.value = true
    isSpeakerMappingDialogOpen.value = false
    speakerMappingSuccessMessage.value = t('game.speaker_mapping.fixed')
    speakerMappingSuccess.value = true
    await Promise.all([getSpeakerMapping(), getEvaluationRequests()])
  }
  catch (error: any) {
    reportSpeakerMappingError(error)
  }
  finally {
    loadingFixParticipants.value = false
  }
}

const processExpertRequest = (alreadyCreated: boolean = false) => {
  ExpertRequestAlreadyCreated.value = alreadyCreated
  getEvaluationRequests()
}

const processExpertAssign = () => {
  getGameData()
}

const loadingRefreshingAIRequest = ref(false)
const refreshAIRequestError = ref(false)
const refreshAIRequestErrorMessage = ref('')

// Причина отказа приходит кодом в теле ответа: «ещё выполняется» и «в записи нет речи» —
// разные ситуации, и общий текст про идущую оценку во втором случае врал бы.
const refreshAIRequestRejectionKeys: Record<string, string> = {
  [AIRerunRejectionReasonEnum.EvaluationNotCompleted]: 'game.ai_expert_refresh_rejected.evaluation_not_completed',
  [AIRerunRejectionReasonEnum.AnswerPending]: 'game.ai_expert_refresh_rejected.answer_pending',
  [AIRerunRejectionReasonEnum.RecognitionPending]: 'game.ai_expert_refresh_rejected.recognition_pending',
  [AIRerunRejectionReasonEnum.RecognitionHasNoSpeech]: 'game.ai_expert_refresh_rejected.recognition_has_no_speech',
  [AIRerunRejectionReasonEnum.RecognitionHasTooLittleSpeech]: 'game.ai_expert_refresh_rejected.recognition_has_too_little_speech',
  [AIRerunRejectionReasonEnum.NoAIEvaluation]: 'game.ai_expert_refresh_rejected.no_ai_evaluation',
  [AIRerunRejectionReasonEnum.GameEvaluationBlocked]: 'game.ai_expert_refresh_rejected.game_evaluation_blocked',
}

// `useApi` на ошибку HTTP не бросает, а оставляет данные пустыми, так что запросы просто не
// обновятся. Отдельный перехват — на сетевой сбой: провалом самой переоценки он не является.
const reloadEvaluationRequests = async (id: string) => {
  try {
    await getEvaluationRequests(id)
  }
  catch (error) {
    console.error(error)
  }
}

// Отдаёт признак того, что переоценка принята. Отказ показывает тостом по причине из тела
// ответа; на 409 страница перечитывает запросы — состояние на бэкенде уже другое.
const startAIRerun = async (id: string): Promise<boolean> => {
  try {
    // Без retry: 0 отказ 409 уходит на бэкенд трижды ($api повторяет этот код по умолчанию),
    // а здесь 409 — не сетевой сбой, а ответ по существу: переоценка уже идёт.
    await $api(`/evaluations/${id}/ai-request/rerun`, { method: 'POST', retry: 0 })

    return true
  }
  catch (error: any) {
    const reason = error?.response?._data?.reason ?? error?.data?.reason

    refreshAIRequestErrorMessage.value = t(refreshAIRequestRejectionKeys[reason] ?? 'game.ai_expert_refresh_failed')
    refreshAIRequestError.value = true

    if ((error?.response?.status ?? error?.status) === 409)
      await reloadEvaluationRequests(id)

    return false
  }
}

// Переоценка — одна операция на бэкенде: второй клик или другая вкладка получают 409, и тогда
// страница перечитывает запросы, чтобы показать идущую оценку.
const refreshAIRequest = async () => {
  const id = route.params.id
  if (!isUuid(id) || loadingRefreshingAIRequest.value) return

  loadingRefreshingAIRequest.value = true
  try {
    const rerunStarted = await startAIRerun(id)
    if (!rerunStarted)
      return

    // POST прошёл — прежние оценки уже удалены и переоценка идёт. Состояние держит локальный
    // признак: баннер появится, а кнопка скроется, даже если перечитать запросы не удалось.
    reevaluationRequested.value = true
    AICreated.value = true
    userStore.shouldUpdateGameComments = true
    userStore.shouldUpdateGameEvaluation = true

    await reloadEvaluationRequests(id)
  }
  finally {
    loadingRefreshingAIRequest.value = false
  }
}

const toDate = (s?: string | null): Date | null => {
  if (!s) return null
  let normalized = s.includes('T') ? s : s.replace(' ', 'T')
  const d = new Date(normalized)
  return Number.isNaN(d.getTime()) ? null : d
}

const durationMinutes = computed(() => {
  const start = toDate(gameData.value.startedAt)
  const end = toDate(gameData.value.endedAt)
  if (!start || !end) return 0
  const diffMs = end.getTime() - start.getTime()
  if (diffMs <= 0) return 0
  return Math.round(diffMs / 60000)
})

const durationAI = 3 * 60 * 1000
const intervalTime = 100
const step = 100 / (durationAI / intervalTime)
const progress = ref(0)
let interval: ReturnType<typeof setInterval> | null = null

const stopInterval = () => {
  if (interval) {
    clearInterval(interval)
    interval = null
  }
}

const startInterval = () => {
  stopInterval()
  interval = setInterval(() => {
    progress.value = Math.min(100, +(progress.value + step).toFixed(4))
    if (progress.value >= 100) stopInterval()
  }, intervalTime)
}

watch(aiEvaluationRunning, (inProgress, wasInProgress) => {
  if (inProgress) {
    progress.value = 0
    startInterval()

    return
  }

  stopInterval()

  // Вкладки оценок и комментариев перечитывают данные только по флагам стора: без них новые
  // AI-оценки после переоценки появились бы лишь после перезагрузки.
  if (wasInProgress) {
    userStore.shouldUpdateGameEvaluation = true
    userStore.shouldUpdateGameComments = true
  }
}, { immediate: true })

onUnmounted(stopInterval)

watch(userStore, () => {
  if (userStore.shouldUpdateGameComments)
    getEvaluationRequests()
})


let currentChannelName: string | null = null

const refreshGameState = async (id: string) => {
  await getGameData(id)
  await Promise.all([
    getVoiceAgentProgress(id),
    getEffectiveness(id),
    getEvaluationRequests(id),
    getSpeakerMapping(id),
  ])

  // Бэкенд шлёт событие при старте и конце переоценки: дальше индикатор ведёт его признак.
  reevaluationRequested.value = false
}

const subscribeToGameChannel = (maybeId?: string) => {
  const id = maybeId ?? route.params.id
  if (!isUuid(id)) return

  const next = `game.${id}`
  if (currentChannelName && currentChannelName !== next) {
    window.Echo.leave(currentChannelName)
  }
  currentChannelName = next

  const channel = window.Echo.private(next)
  channel.stopListening('.game.should.refresh')
  channel.listen('.game.should.refresh', () => refreshGameState(id))
}

onUnmounted(() => {
  if (currentChannelName) window.Echo.leave(currentChannelName)
})

const dialogTarget = ref<HTMLElement | null>(null)
watch(
  () => route.params.id,
  async id => {
    if (!isUuid(id)) return

    isGameLoading.value = true
    reevaluationRequested.value = false
    isNotFound.value = false
    isLoaderDialogVisible.value = false
    isVideoProcessing.value = false
    try {
      const found = await getGameData(id)
      if (!found) {
        isNotFound.value = true
        await router.replace('/')
        return
      }
      await Promise.all([
        getEvaluationRequests(id),
        getEffectiveness(id),
        getSpeakerMapping(id),
      ])
      subscribeToGameChannel(id)
      if (isGameVsAI()) await getVoiceAgentProgress(id)
    } catch (e) {
      console.error(e)
      isNotFound.value = true
    } finally {
      isGameLoading.value = false
    }
  },
  { immediate: true },
)

const userIsAdminOrManager = computed(() => userIsAdmin(userRoles) || userIsManager(userRoles) || userIsHead(userRoles))

// Определение уровня оценки для цветовой индикации (для AI игр)
const getScoreLevel = (score: number): string => {
  if (score >= 800) return 'excellent'
  if (score >= 500) return 'good'
  return 'low'
}

onMounted(() => {
  dialogTarget.value = document.getElementsByClassName('layout-page-content')[0] as HTMLElement
})

// Тост "Game already finished" — флаг передаётся из PlayAgentGame при редиректе
// после финализации сессии (Task 10/11). Локальный VSnackbar, потому что глобального
// snackbar-store в проекте пока нет, а тащить Pinia только ради одного тоста — overkill.
const showGameFinishedToast = ref(false)

// Экран сессии не дождался ответа на завершение: разговор закроется на сервере по времени.
const showEndNotSentToast = ref(false)

onMounted(() => {
  const justFinished = route.query.just_finished === '1'
  const endNotSent = route.query.end_not_sent === '1'

  if (justFinished || endNotSent) {
    showGameFinishedToast.value = justFinished
    showEndNotSentToast.value = endNotSent

    // Убираем query, чтобы deeplink на ту же страницу не дёргал тост заново
    // (replace, а не push, чтобы не засорять history). Используем существующий router из :179.
    router.replace({ query: { ...route.query, just_finished: undefined, end_not_sent: undefined } })
  }
})
</script>

<template>
  <div
    v-if="aiEvaluationInProgress"
    class="flex d-flex justify-center align-center text-center text-warning mb-2"
  >
    <VIcon
      start
      icon="tabler-wand"
    />{{ t('game.ai_evaluation_in_progress') }}
  </div>
  <VProgressLinear
    v-if="aiEvaluationInProgress"
    color="rgb(var(--v-theme-warning))"
    :model-value="progress"
    striped
    class="mb-6"
  />
  <VSnackbar
    v-model="AICreated"
    transition="scroll-y-reverse-transition"
    location="top end"
    color="success"
  >
    {{ t('game.ai_expert_created') }}
    <template #actions>
      <VBtn
        size="20"
        color="white"
        variant="plain"
        @click="AICreated = false"
      >
        <VIcon
          icon="tabler-x"
          size="20"
        />
      </VBtn>
    </template>
  </VSnackbar>
  <VSnackbar
    v-model="ExpertRequestAlreadyCreated"
    transition="scroll-y-reverse-transition"
    location="top end"
    color="success"
  >
    {{ t('game.expert_already_requested') }}
    <template #actions>
      <VBtn
        size="20"
        color="white"
        variant="plain"
        @click="ExpertRequestAlreadyCreated = false"
      >
        <VIcon
          icon="tabler-x"
          size="20"
        />
      </VBtn>
    </template>
  </VSnackbar>
  <VSnackbar
    v-model="showGameFinishedToast"
    transition="scroll-y-reverse-transition"
    location="top end"
    color="success"
  >
    {{ t('ai-game.voice.game_already_finished') }}
    <template #actions>
      <VBtn
        size="20"
        color="white"
        variant="plain"
        @click="showGameFinishedToast = false"
      >
        <VIcon
          icon="tabler-x"
          size="20"
        />
      </VBtn>
    </template>
  </VSnackbar>
  <VSnackbar
    v-model="showEndNotSentToast"
    transition="scroll-y-reverse-transition"
    location="top end"
    color="warning"
  >
    {{ t('voiceSession.end.notSent') }}
    <template #actions>
      <VBtn
        size="20"
        color="white"
        variant="plain"
        @click="showEndNotSentToast = false"
      >
        <VIcon
          icon="tabler-x"
          size="20"
        />
      </VBtn>
    </template>
  </VSnackbar>
  <VSnackbar
    v-model="speakerMappingSuccess"
    transition="scroll-y-reverse-transition"
    location="top end"
    color="success"
  >
    {{ speakerMappingSuccessMessage }}
    <template #actions>
      <VBtn
        size="20"
        color="white"
        variant="plain"
        @click="speakerMappingSuccess = false"
      >
        <VIcon
          icon="tabler-x"
          size="20"
        />
      </VBtn>
    </template>
  </VSnackbar>
  <VSnackbar
    v-model="refreshAIRequestError"
    transition="scroll-y-reverse-transition"
    location="top end"
    color="error"
  >
    {{ refreshAIRequestErrorMessage }}
    <template #actions>
      <VBtn
        size="20"
        color="white"
        variant="plain"
        @click="refreshAIRequestError = false"
      >
        <VIcon
          icon="tabler-x"
          size="20"
        />
      </VBtn>
    </template>
  </VSnackbar>
  <VSnackbar
    v-model="speakerMappingError"
    transition="scroll-y-reverse-transition"
    location="top end"
    color="error"
  >
    {{ t('game.speaker_mapping.action_failed') }}
    <br v-if="speakerMappingErrorMessage">
    {{ speakerMappingErrorMessage }}
    <template #actions>
      <VBtn
        size="20"
        color="white"
        variant="plain"
        @click="speakerMappingError = false"
      >
        <VIcon
          icon="tabler-x"
          size="20"
        />
      </VBtn>
    </template>
  </VSnackbar>
  <div>
    <div v-if="isGameLoading">
      <VProgressLinear
        color="primary"
        :height="8"
        class="rounded-e-0 rounded-lg mb-6"
        indeterminate />
    </div>
    <!-- 👉 Header  -->
    <div
      v-else-if="gameData.number !== 0"
      class="d-flex justify-space-between align-center flex-wrap gap-y-4 mb-6"
    >
      <div class="d-flex gap-3 align-center flex-wrap">
        <VAvatar
          size="48"
          color="primary"
          variant="tonal"
          rounded
        >
          <VIcon
            icon="tabler-trophy"
            size="24"
          />
        </VAvatar>
        <div>
          <div class="d-flex gap-2 align-center flex-wrap">
            <h4
              v-if="!hideGameNumber"
              class="text-h4 font-weight-bold"
            >
              {{ t('game.title') }} #{{ gameData.number }}
            </h4>
            <h4
              v-else
              class="text-h4 font-weight-bold"
            >
              {{ t('game.title') }}
            </h4>
            <VBadge
              v-if="evaluationRequests?.filter(r => r.evaluatorType === 'ai').length > 0"
              content="ai"
              :color="evaluationRequests.filter(r => r.evaluatorType === 'ai')[0]?.status === 'completed' ? 'success' : 'warning'"
            >
              <VChip
                :color="resolveStatusVariant(gameData.status).color"
                size="small"
              >
                {{ resolveStatusVariant(gameData.status).text }}
              </VChip>
            </VBadge>
            <VChip
              v-else
              :color="resolveStatusVariant(gameData.status).color"
              size="small"
            >
              {{ resolveStatusVariant(gameData.status).text }}
            </VChip>
            <VChip
              v-if="isGameUploadedRecording()"
              color="info"
              variant="tonal"
              size="small"
            >
              <VIcon
                start
                icon="tabler-file-upload"
                size="16"
              />
              {{ t('games-page.game_types.uploaded_recording') }}
            </VChip>
            <!-- Участники AI-оценки: чип открывает диалог с цитатами, жёлтый — если нужна проверка -->
            <VChip
              v-if="speakerMapping"
              :color="speakerMapping.needsReview ? 'warning' : 'secondary'"
              variant="tonal"
              size="small"
              @click="isSpeakerMappingDialogOpen = true"
            >
              <VIcon
                start
                :icon="speakerMapping.needsReview ? 'tabler-alert-triangle' : 'tabler-users'"
                size="16"
              />
              {{ speakerMapping.needsReview ? t('game.speaker_mapping.needs_review') : t('game.speaker_mapping.title') }}
            </VChip>
          </div>
        </div>
      </div>
      <div
        v-if="isGameVsAI()"
        class="d-flex gap-4"
      >
        <VBtn
          v-if="userIsAdminOrManager"
          variant="tonal"
          color="primary"
          @click="isGameExpertAssignDialogOpen = true"
        >
          <VIcon
            start
            icon="tabler-user-check"
          />
          {{ t('game.assign_expert') }}
        </VBtn>
      </div>
        <div
        v-if="isGameVsPlayer() || isGameUploadedRecording()"
        class="d-flex gap-4"
      >
        <VBtn
          v-if="!userIsGameExpert && (showRequestExpertButton || (ExpertRequest?.status === 'in_progress' || ExpertRequest?.status === 'queued'))"
          variant="tonal"
          :loading="ExpertRequest?.status === 'in_progress' || ExpertRequest?.status === 'queued'"
          :disabled="ExpertRequest?.status === 'in_progress' || ExpertRequest?.status === 'queued'"
          color="info"
          @click="isGameExpertDialogOpen = true"
        >
          <VIcon
            start
            icon="tabler-user"
          />
          {{ t('game.request_expert') }}
          <template #loader>
            <span class="custom-loader">
              <VIcon icon="tabler-refresh" />
            </span>
            <span class="ml-1">{{ t('game.request_expert') }}</span>
          </template>
        </VBtn>
        <VBtn
          v-if="showAIExpertRefreshBtn()"
          variant="tonal"
          color="warning"
          :loading="loadingRefreshingAIRequest"
          :disabled="loadingRefreshingAIRequest"
          @click="refreshAIRequest"
        >
          <VIcon
            start
            icon="tabler-refresh"
          />
          {{ t('game.ai_expert_refresh') }}
        </VBtn>
        <div
          v-if="showAIExpertRequestBtn() && aiEvaluationBlockReason !== null"
          class="d-flex align-center text-body-2 text-medium-emphasis"
        >
          {{ aiEvaluationBlockMessage }}
        </div>
        <VBtn
          v-if="showAIExpertRequestBtn()"
          color="success"
          :loading="aiEvaluationInProgress"
          :disabled="AIRequest.status !== null || aiEvaluationBlockReason !== null"
          @click="createAIRequest"
        >
          <VIcon
            v-if="AIRequest.status !== 'completed'"
            start
            icon="tabler-wand"
          />
          <VIcon
            v-if="AIRequest.status === 'completed'"
            start
            icon="tabler-check"
          />
          {{ t('game.ai_expert') }}
          <template #loader>
            <span class="custom-loader">
              <VIcon icon="tabler-refresh" />
            </span>
            <span class="ml-1">{{ t('game.ai_expert') }}</span>
          </template>
        </VBtn>

        <VTooltip location="top">
          <template #activator="{ props }">
            <VBtn
              v-if="showDeleteAIRequestBtn()"
              v-bind="props"
              variant="tonal"
              :loading="loadingDeletingAIRequest"
              :disabled="loadingDeletingAIRequest"
              color="error"
              icon="tabler-trash"
              @click="deleteAIRequest"
            >
              <template #loader>
                <span class="custom-loader">
                  <VIcon icon="tabler-refresh" />
                </span>
              </template>
            </VBtn>
          </template>
          <span>{{ t('game.delete_ai_request') }}</span>
        </VTooltip>

        <VBtn
          v-if="userIsGameExpert && ExpertRequest.status === 'in_progress'"
          color="success"
          @click="expertComplete"
        >
          <VIcon
            start
            icon="tabler-check"
          />
          {{ t('game.complete_expert_request') }}
          <template #loader>
            <span class="custom-loader">
              <VIcon icon="tabler-refresh" />
            </span>
            <span class="ml-1">{{ t('game.complete_expert_request') }}</span>
          </template>
        </VBtn>
      </div>
    </div>
    <!-- 👉 Game Stats Cards -->
    <VRow
      v-if="gameData.number !== 0"
      class="match-height mb-2"
    >
      <!-- Карточка со счётом: Игрок vs Игрок и запись с двумя сторонами -->
      <VCol
        v-if="isOpposingTeamsScore"
        cols="12"
        lg="5"
        md="6"
      >
        <VCard class="game-score-card h-100">
          <VCardText class="pa-4 d-flex align-center h-100">
            <div class="d-flex align-center justify-space-between w-100">
              <!-- Команда A -->
              <div class="team-section">
                <div class="d-flex align-center gap-3">
                  <div class="v-avatar-group">
                    <template
                      v-for="participant in gameData.participants"
                      :key="participant.user.id"
                    >
                      <VAvatar
                        v-if="participant.team === 'A'"
                        size="40"
                        :color="!participant.user.profile.has_avatar ? resolveAvatarVariant(participant.role) : undefined"
                        :variant="!participant.user.profile.has_avatar ? 'tonal' : undefined"
                      >
                        <VImg
                          v-if="participant.user.profile.has_avatar"
                          :src="`/api/account/avatar-${participant.user.id}`"
                        />
                        <span v-else class="text-caption">{{ participant.user.profile.name.slice(0, 1).toUpperCase() + participant.user.profile.last_name.slice(0, 1).toUpperCase() }}</span>
                        <VTooltip
                          activator="parent"
                          location="top"
                          max-width="300"
                        >
                          <div
                            v-dompurify-html="getParticipantHint(participant)"
                            class="px-2"
                          />
                        </VTooltip>
                      </VAvatar>
                    </template>
                  </div>
                  <div
                    class="score-badge"
                    :class="{ 'winner': (winnerIsA && gameData.isResultsVisible) || (winnerIsA && userIsAdminOrManager) }"
                  >
                    <VIcon
                      v-if="(winnerIsA && gameData.isResultsVisible) || (winnerIsA && userIsAdminOrManager)"
                      icon="tabler-crown"
                      size="18"
                      color="warning"
                    />
                    <span class="score-value text-h5">
                      {{ (gameData.isResultsVisible || userIsAdminOrManager) ? (effectivenessTeamA?.total ?? 0) : '-' }}
                    </span>
                    <span class="text-caption text-medium-emphasis">{{ t('game.points') }}</span>
                  </div>
                </div>
              </div>

              <!-- VS Badge -->
              <div class="vs-section">
                <div class="vs-badge">
                  <span class="text-caption font-weight-bold">VS</span>
                </div>
              </div>

              <!-- Команда B -->
              <div class="team-section text-end">
                <div class="d-flex align-center gap-3 justify-end">
                  <div
                    class="score-badge"
                    :class="{ 'winner': (!winnerIsA && gameData.isResultsVisible) || (!winnerIsA && userIsAdminOrManager) }"
                  >
                    <span class="text-caption text-medium-emphasis">{{ t('game.points') }}</span>
                    <span class="score-value text-h5">
                      {{ (gameData.isResultsVisible || userIsAdminOrManager) ? (effectivenessTeamB?.total ?? 0) : '-' }}
                    </span>
                    <VIcon
                      v-if="(!winnerIsA && gameData.isResultsVisible) || (!winnerIsA && userIsAdminOrManager)"
                      icon="tabler-crown"
                      size="18"
                      color="warning"
                    />
                  </div>
                  <div class="v-avatar-group">
                    <template
                      v-for="participant in gameData.participants"
                      :key="participant.user.id"
                    >
                      <VAvatar
                        v-if="participant.team === 'B'"
                        size="40"
                        :color="!participant.user.profile.has_avatar ? resolveAvatarVariant(participant.role) : undefined"
                        :variant="!participant.user.profile.has_avatar ? 'tonal' : undefined"
                      >
                        <VImg
                          v-if="participant.user.profile.has_avatar"
                          :src="`/api/account/avatar-${participant.user.id}`"
                        />
                        <span v-else class="text-caption">{{ participant.user.profile.name.slice(0, 1).toUpperCase() + participant.user.profile.last_name.slice(0, 1).toUpperCase() }}</span>
                        <VTooltip
                          activator="parent"
                          location="top"
                          max-width="300"
                        >
                          <div
                            v-dompurify-html="getParticipantHint(participant)"
                            class="px-2"
                          />
                        </VTooltip>
                      </VAvatar>
                    </template>
                  </div>
                </div>
              </div>
            </div>
          </VCardText>
        </VCard>
      </VCol>

      <!-- Карточка со счётом: Игрок vs AI и запись с одним игроком -->
      <VCol
        v-else
        cols="12"
        lg="5"
        md="6"
      >
        <VCard class="game-score-card h-100">
          <VCardText class="pa-4 d-flex align-center h-100">
            <template
              v-for="participant in gameData.participants"
              :key="participant.user.id"
            >
              <div
                v-if="participant.team === 'A'"
                class="d-flex align-center gap-3"
              >
                <!-- Аватар игрока -->
                <VAvatar
                  size="44"
                  :color="!participant.user.profile.has_avatar ? resolveAvatarVariant(participant.role) : undefined"
                  :variant="!participant.user.profile.has_avatar ? 'tonal' : undefined"
                >
                  <VImg
                    v-if="participant.user.profile.has_avatar"
                    :src="`/api/account/avatar-${participant.user.id}`"
                  />
                  <span v-else>{{ participant.user.profile.name.slice(0, 1).toUpperCase() + participant.user.profile.last_name.slice(0, 1).toUpperCase() }}</span>
                </VAvatar>

                <!-- Имя игрока -->
                <div>
                  <div class="text-body-1 font-weight-medium">
                    {{ participant.user.profile.name }} {{ participant.user.profile.last_name }}
                  </div>
                  <div class="text-caption text-disabled">
                    {{ t('game.your_result') }}
                  </div>
                </div>

                <!-- Счёт игрока -->
                <div
                  class="score-badge ai-score ms-2"
                  :class="[`score-${getScoreLevel(effectivenessTeamA?.total ?? 0)}`]"
                >
                  <VIcon
                    v-if="gameData.isResultsVisible || userIsAdminOrManager"
                    :icon="(effectivenessTeamA?.total ?? 0) >= 800 ? 'tabler-trophy' : (effectivenessTeamA?.total ?? 0) >= 500 ? 'tabler-thumb-up' : 'tabler-trending-up'"
                    size="20"
                  />
                  <span class="score-value text-h5">
                    {{ (gameData.isResultsVisible || userIsAdminOrManager) ? (effectivenessTeamA?.total ?? 0) : '-' }}
                  </span>
                  <span class="text-body-2">{{ t('game.points') }}</span>
                </div>
              </div>
            </template>
          </VCardText>
        </VCard>
      </VCol>

      <!-- Карточка с компетенцией -->
      <VCol
        cols="12"
        lg="4"
        md="6"
        sm="6"
      >
        <VCard class="h-100">
          <VCardText class="pa-4 d-flex align-center h-100">
            <div class="d-flex align-center gap-3 w-100">
              <VAvatar
                size="40"
                color="primary"
                variant="tonal"
                rounded
                class="flex-shrink-0"
              >
                <VIcon
                  icon="tabler-target"
                  size="20"
                />
              </VAvatar>
              <div class="overflow-hidden">
                <div
                  class="text-body-2 font-weight-bold text-truncate"
                  :title="gameData.competenceTitle"
                >
                  {{ gameData.competenceTitle }}
                </div>
                <div class="text-caption text-disabled">
                  {{ t('statistics.competence') }}
                </div>
              </div>
            </div>
          </VCardText>
        </VCard>
      </VCol>

      <!-- Карточка с длительностью и датой -->
      <VCol
        cols="12"
        lg="3"
        md="6"
        sm="6"
      >
        <VCard class="h-100">
          <VCardText class="pa-4 d-flex align-center h-100">
            <div class="d-flex align-center gap-4 w-100">
              <div class="d-flex align-center gap-2">
                <VAvatar
                  size="36"
                  color="info"
                  variant="tonal"
                  rounded
                >
                  <VIcon
                    icon="tabler-clock"
                    size="18"
                  />
                </VAvatar>
                <div>
                  <div class="text-body-1 font-weight-bold">
                    {{ durationMinutes }} <span class="text-caption text-disabled">{{ t('game.min') }}</span>
                  </div>
                </div>
              </div>
              <VDivider vertical />
              <div class="d-flex align-center gap-2">
                <VAvatar
                  size="36"
                  color="secondary"
                  variant="tonal"
                  rounded
                >
                  <VIcon
                    icon="tabler-calendar"
                    size="18"
                  />
                </VAvatar>
                <div>
                  <div class="text-body-2 font-weight-medium">
                    {{ formatDateToMonthShort(gameData?.endedAt ?? '2024-10-10 10:10', true, dateLocale, userData.timezone) }}
                  </div>
                </div>
              </div>
            </div>
          </VCardText>
        </VCard>
      </VCol>

      <!-- Участники AI-оценки требуют проверки: строка с действиями появляется только в этом случае -->
      <VCol
        v-if="speakerMapping?.needsReview"
        cols="12"
      >
        <VAlert
          type="warning"
          variant="tonal"
        >
          <div class="d-flex align-center justify-space-between flex-wrap gap-3">
            <div>
              <div class="font-weight-medium">
                {{ t('game.speaker_mapping.needs_review') }}
              </div>
              <div class="text-body-2">
                {{ speakerMappingReviewHint }}
              </div>
            </div>
            <div class="d-flex flex-wrap gap-2">
              <VBtn
                size="small"
                color="success"
                variant="tonal"
                :loading="loadingConfirmParticipants"
                :disabled="loadingConfirmParticipants || loadingFixParticipants"
                @click="confirmParticipants"
              >
                {{ t('game.speaker_mapping.confirm') }}
              </VBtn>
              <VBtn
                size="small"
                color="warning"
                :disabled="loadingConfirmParticipants || loadingFixParticipants"
                @click="isSpeakerMappingDialogOpen = true"
              >
                {{ t('game.speaker_mapping.review') }}
              </VBtn>
            </div>
          </div>
        </VAlert>
      </VCol>

      <VCol cols="12">
        <VTabs
          v-model="userTab"
          class="v-tabs-pill mb-3 disable-tab-transition"
        >
          <VTab
            v-for="tab in tabs"
            :key="tab.title"
            :value="tab.title"
          >
            <VIcon
              size="20"
              start
              :icon="tab.icon"
            />
            {{ t(`game.tabs.${tab.title}.title`) }}
          </VTab>
        </VTabs>
        <VWindow
          v-model="userTab"
          class="disable-tab-transition"
          :touch="false"
        >
          <VWindowItem value="comments">
            <GameTabComments
              :url="gameData.recordUrl"
              :game-id="gameData.id"
              :is-vs-ai="isGameVsAI()"
              :is-checklist="isChecklist"
              :is-completed="isGameCompleted"
              :hide-ai-results="!gameData.isResultsVisible"
              :user-is-player="userIsCurrentGamePlayer"
              :user-is-admin="userIsAdmin(userRoles)"
              :user-is-manager="userIsManager(userRoles)"
              :user-is-expert="userIsExpert(userRoles)"
              :user-is-head="userIsHead(userRoles)"
              :evaluation-requests="evaluationRequests"
              :is-video-processing="isVideoProcessing"
              :players="gamePlayers.map((participant) => ({ id: participant.user.profile.user_id, fullName: `${participant.user.profile.name} ${participant.user.profile.last_name}` }))"
            />
          </VWindowItem>
          <VWindowItem
            v-if="isChecklist"
            value="checklist"
          >
            <GameTabChecklist :game-id="gameData.id" />
          </VWindowItem>
          <VWindowItem
            v-if="!isChecklist"
            value="feedback"
          >
            <GameTabFeedback
              :game-id="gameData.id"
              :scenario-id="gameData.scenarioId"
              :hide-ai-results="!gameData.isResultsVisible"
              :is-each-other-rated="gameData.isEachOtherRated"
              :is-self-rated="gameData.isSelfRated"
              :user-is-admin="userIsAdmin(userRoles)"
              :user-is-manager="userIsManager(userRoles)"
              :user-is-expert="userIsExpert(userRoles)"
              :user-is-head="userIsHead(userRoles)"
              :user-is-player="userIsCurrentGamePlayer"
              :players="gamePlayers.map((participant) => ({ id: participant.user.profile.user_id, evaluations: [], fullName: `${participant.user.profile.name} ${participant.user.profile.last_name}` }))"
            />
          </VWindowItem>
          <VWindowItem
            v-if="!isChecklist"
            value="results"
          >
            <GameTabResults
              :game-id="gameData.id"
              :players-count="gamePlayers.length"
              :effectiveness-team-a="effectivenessTeamA"
              :effectiveness-team-b="effectivenessTeamB"
              :is-vs-ai="isGameVsAI()"
              :participants="gamePlayers"
            />
          </VWindowItem>
          <VWindowItem value="description">
            <GameTabDescription
              :game-id="gameData.id"
              :type="gameData.type"
            />
          </VWindowItem>
        </VWindow>
      </VCol>
    </VRow>
    <div v-else-if="isNotFound">
      <VAlert
        type="error"
        variant="tonal"
      >
        Game with ID  {{ route.params.id }} not found!
      </VAlert>
    </div>
  </div>
  <GameExpertRequestDialog
    v-model:isDialogVisible="isGameExpertDialogOpen"
    @submit="processExpertRequest"
  />
  <GameExpertAssignDialog
    v-model:isDialogVisible="isGameExpertAssignDialogOpen"
    :current-expert-id="gameData.expertId"
    @submit="processExpertAssign"
  />
  <GameSpeakerMappingDialog
    v-if="speakerMapping"
    v-model:isDialogVisible="isSpeakerMappingDialogOpen"
    :mapping="speakerMapping"
    :participants="speakerMappingParticipants"
    :is-confirming="loadingConfirmParticipants"
    :is-saving="loadingFixParticipants"
    @confirm="confirmParticipants"
    @save="saveSpeakerMapping"
  />
  <VDialog
    v-model="isLoaderDialogVisible"
    width="300"
    persistent
    contained
    location="start"
    :offset="20"
    :attach="dialogTarget"
    scroll-strategy="none"
    content-class="loader-dialog-content"
  >
    <VCard
      color="primary"
      width="300"
    >
      <VCardText class="pt-3">
        {{ t('game.analyzing_results') }}
        <VProgressLinear
          indeterminate
          bg-color="rgba(var(--v-theme-surface), 0.1)"
          :height="8"
          class="mb-0 mt-4"
        />
      </VCardText>
    </VCard>
  </VDialog>
</template>

<style lang="scss">
.custom-loader {
  display: flex;
  animation: loader 1s infinite;
}

.layout-page-content {
  position: relative !important;
}

.loader-dialog-content {
  top: 100px !important;
}

@keyframes loader {
  from {
    transform: rotate(0);
  }

  to {
    transform: rotate(360deg);
  }
}

// Game Score Card Styles
.game-score-card {
  .team-section {
    flex: 1;
    max-inline-size: 45%;
  }

  .score-badge {
    display: inline-flex;
    gap: 6px;
    align-items: center;
    padding: 8px 14px;
    border-radius: 10px;
    background: rgba(var(--v-theme-on-surface), 0.06);
    transition: all 0.2s ease;

    .score-value {
      font-weight: 700;
      background: linear-gradient(135deg, rgb(var(--v-theme-primary)) 0%, rgb(var(--v-theme-info)) 100%);
      background-clip: text;
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }

    &.winner {
      background: linear-gradient(135deg, rgba(var(--v-theme-warning), 0.15) 0%, rgba(var(--v-theme-warning), 0.05) 100%);
      box-shadow: 0 4px 20px rgba(var(--v-theme-warning), 0.15);

      .score-value {
        background: linear-gradient(135deg, rgb(var(--v-theme-warning)) 0%, #ff8c00 100%);
        background-clip: text;
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
      }
    }

    // Стили для AI игр с цветовой индикацией
    &.ai-score {
      &.score-low {
        background: linear-gradient(135deg, rgba(var(--v-theme-error), 0.18) 0%, rgba(var(--v-theme-error), 0.08) 100%);
        color: rgb(var(--v-theme-error));

        .score-value {
          background: linear-gradient(135deg, rgb(var(--v-theme-error)) 0%, #ff6b6b 100%);
          background-clip: text;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }
      }

      &.score-good {
        background: linear-gradient(135deg, rgba(var(--v-theme-warning), 0.18) 0%, rgba(var(--v-theme-warning), 0.08) 100%);
        color: rgb(var(--v-theme-warning));

        .score-value {
          background: linear-gradient(135deg, rgb(var(--v-theme-warning)) 0%, #ff8c00 100%);
          background-clip: text;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }
      }

      &.score-excellent {
        position: relative;
        overflow: hidden;
        background: linear-gradient(135deg, rgba(var(--v-theme-success), 0.2) 0%, rgba(var(--v-theme-success), 0.1) 100%);
        box-shadow: 0 4px 24px rgba(var(--v-theme-success), 0.25), 0 0 0 1px rgba(var(--v-theme-success), 0.1) inset;
        color: rgb(var(--v-theme-success));

        .score-value {
          background: linear-gradient(135deg, rgb(var(--v-theme-success)) 0%, #2ecc71 100%);
          background-clip: text;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        // Эффект мерцания для отличного результата
        &::before {
          position: absolute;
          inset-block-start: 0;
          inset-inline-start: -100%;
          z-index: 0;
          inline-size: 80%;
          block-size: 100%;
          background: linear-gradient(
            90deg,
            transparent 0%,
            rgba(255, 255, 255, 0.4) 50%,
            transparent 100%
          );
          animation: shimmer 2.5s ease-in-out infinite;
          content: "";
        }

        > * {
          position: relative;
          z-index: 1;
        }
      }
    }
  }

  @keyframes shimmer {
    0% {
      inset-inline-start: -100%;
      opacity: 0;
    }

    20% {
      opacity: 1;
    }

    80% {
      opacity: 1;
    }

    100% {
      inset-inline-start: 200%;
      opacity: 0;
    }
  }

  .vs-section {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    padding-inline: 12px;
  }

  .vs-badge {
    display: flex;
    align-items: center;
    justify-content: center;
    inline-size: 36px;
    block-size: 36px;
    border-radius: 50%;
    background: rgba(var(--v-theme-on-surface), 0.08);
  }

  .ai-avatar {
    background: linear-gradient(135deg, rgb(143 0 255 / 20%) 0%, rgb(143 0 255 / 40%) 100%);
    color: #8f00ff;
  }
}
</style>
